import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import _ from 'lodash';
import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { userPrivileges, userTier } from '../../../../../apollo/cache';
import AppraiserForm from '../../../../AppraiserForm/components/AppraiserForm';
import AppraiserDataMapper from '../../../../AppraiserForm/mappers/AppraiserDataMapper';
import ApolloError from '../../../../shared/components/ApolloError';
import Nbsp from '../../../../shared/components/Nbsp';
import SkeletonWrapper from '../../../../shared/components/skeleton/SkeletonWrapper';
import { getDataNotFoundPage } from '../../../../shared/services/getDataNotFoundPageService';
import EDIT_APPRAISER from '../../shared/queries/editAppraiserMutation';
import GET_APPRAISER_INFO from '../../shared/queries/getAppraiserInfoQuery';
import AppraiserAppraisalAssignmentSection from './AppraiserAppraisalAssignmentSection';
import PrivilegeNames from '../../../../../constants/privilegeNames';
import * as systemRoles from '../../../../../constants/system-roles';
import { Page } from '../../../../layout';
import { useAppraiserTabs } from '../../shared/hooks/useAppraiserTabs';
import { Breadcrumb } from '@shared/components/breadcrumb/Breadcrumb';
import ResponsiveUrlTabMenu from '@shared/components/ResponsiveUrlTabMenu';
import * as LicenseTypes from '../../../constants/LicenseTypes';
// eslint-disable-next-line unused-imports/no-unused-imports -- referenced by the props typedef
import { DEFAULT_APPRAISER_DATA } from '@features/AppraiserForm/constants/AppraiserInfoConstants';

const AppraiserProfilePage = () => {
  const { orgId, userId } = useParams();
  const { state } = useLocation();

  const tier = useReactiveVar(userTier);
  const privileges = useReactiveVar(userPrivileges);
  const canEditDirectChannel = privileges.includes(
    PrivilegeNames.AppraiserProfile_DirectChannel_Edit
  );
  const canEditObservation = privileges.includes(PrivilegeNames.AppraiserProfile_Observation_Edit);
  const canEditAdditionalInformation = privileges.includes(
    PrivilegeNames.AppraiserProfile_AdditionalInformation_Edit
  );
  const previousLink = privileges.includes(PrivilegeNames.OrganizationSearch_Advanced)
    ? `/user-management/${orgId}`
    : '/user-management';
  const pageTitle = 'Appraiser Profile';
  const breadcrumbPathItems = [
    {
      name: 'User Management',
      url: previousLink
    },
    { name: pageTitle, url: '' }
  ];

  const [appraiserFormData, setAppraiserFormData] = useState(
    /** @type {typeof DEFAULT_APPRAISER_DATA} */ ({})
  );

  const navigate = useNavigate();

  const {
    appraiserTabs,
    loading: appraiserTabsLoading,
    error: appraiserTabsError
  } = useAppraiserTabs(userId);

  const {
    data: appraiserInfoData,
    loading: appraiserInfoLoading,
    error: appraiserInfoError
  } = useQuery(GET_APPRAISER_INFO, {
    variables: { externalUserId: parseInt(userId) },
    onCompleted: appraiserInfoData => {
      if (!appraiserInfoData.appraiserInfo) {
        return;
      }

      setAppraiserFormData(
        AppraiserDataMapper.appraiserDataToEditFields(appraiserInfoData.appraiserInfo)
      );
    }
  });

  const appraiserNonEditableData = appraiserInfoData?.appraiserInfo
    ? AppraiserDataMapper.appraiserDataToViewFields(appraiserInfoData.appraiserInfo)
    : {};

  const [editAppraiser, { error: editAppraiserError }] = useMutation(EDIT_APPRAISER);

  const isFormEdited =
    appraiserInfoData &&
    appraiserInfoData.appraiserInfo &&
    !_.isEqual(
      appraiserFormData,
      AppraiserDataMapper.appraiserDataToEditFields(appraiserInfoData.appraiserInfo)
    );

  const saveAppraiser = async editedDomainExperiences => {
    const appraiserFormInput = AppraiserDataMapper.appraiserDataToApolloInput(
      appraiserFormData,
      editedDomainExperiences
    );

    await editAppraiser({
      variables: {
        externalUserId: parseInt(userId),
        appraiser: appraiserFormInput
      }
    });

    navigate(
      `${previousLink}#${state?.isFree ? LicenseTypes.FREE.value : LicenseTypes.PAID.value}`,
      {
        state: {
          licensesModifiedSuccessfullyMessage: 'Edits Submitted Successfully.'
        }
      }
    );
  };

  if (appraiserInfoData && !appraiserInfoData.appraiserInfo) {
    return <Navigate to={getDataNotFoundPage(tier)} />;
  }

  if (appraiserInfoError || editAppraiserError || appraiserTabsError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle}>
      <Breadcrumb pathItems={breadcrumbPathItems} />
      <div className='user-management-tabs'>
        <ResponsiveUrlTabMenu
          tabs={appraiserTabs}
          urlParams={{
            orgId,
            userId
          }}
          loading={appraiserTabsLoading}
        />
      </div>
      <div className='appraiser-profile-page'>
        <div className='appraiser-name'>
          <p className='bold'>Appraiser Profile:</p>
          <Nbsp />
          <SkeletonWrapper show={appraiserInfoLoading} inheritParentSize={true}>
            <p className='bold'>{appraiserNonEditableData.name}</p>
          </SkeletonWrapper>
        </div>
        <AppraiserForm
          appraiserFormData={appraiserFormData}
          appraiserNonEditableData={appraiserNonEditableData}
          setAppraiserFormData={setAppraiserFormData}
          appraiserInfoLoading={appraiserInfoLoading}
          isFormEdited={isFormEdited}
          saveAppraiser={saveAppraiser}
          previousLink={previousLink}
          canEditDirectChannel={canEditDirectChannel}
          canEditObservation={canEditObservation}
          canEditAdditionalInformation={canEditAdditionalInformation}
        />

        {privileges.includes(PrivilegeNames.AppraiserProfile_AtlAssignment) &&
          appraiserNonEditableData.systemRole?.id === systemRoles.ISACA_MDDAP_APPRAISER && (
            <AppraiserAppraisalAssignmentSection
              assignedAppraisals={appraiserFormData.assignedAppraisals}
              autoAssignedAppraisals={appraiserNonEditableData.autoAssignedAppraisals}
              setAppraiserFormData={setAppraiserFormData}
              loading={appraiserInfoLoading}
            />
          )}
      </div>
    </Page>
  );
};

export default AppraiserProfilePage;
